import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Shield, Lock, Unlock, AlertTriangle, FileText, Clock, Target,
  DollarSign, Music, Calendar, Crosshair, ArrowLeft, Mic, Guitar,
  Globe, Tv, Film, Gamepad2, Activity
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { vaultConfig } from "../data/mock";
import trackingService from "../services/trackingService";

const activeBriefs = [
  {
    id: "brief-transport-commercial",
    title: "Funky Pop Instrumental — Brand Commercial: Transport",
    client: "Transportation Brand (Undisclosed)",
    type: "Brand Commercial",
    deadline: "2026-05-06",
    payout: "$50,000",
    payoutRaw: 50000,
    status: "ACTIVE",
    priority: "CRITICAL",
    description: "A creative director is seeking a fun, rhythmic pop instrumental for a transportation brand commercial centered around college students moving off campus at the end of the school year. The ideal track shall be upbeat, energetic, and groovy. Instrumental only, but should carry the same vibe as the reference.",
    genres: ["Funk Pop", "Indie Pop", "Groove Pop"],
    vocal: "Instrumental Only",
    use: "TV Broadcast, Internet, Paid Media",
    length: "Full (flexible length)",
    versions: "Cutdowns, multiple edits, versions, lifts",
    paidMedia: "4 weeks",
    term: "Worldwide, All Media, In Perpetuity",
    references: [
      "Chromeo — Jealous (instrumental)",
      "Young Franco — Rollout (instrumental)",
      "Parcels — Tied up right now (instrumental)"
    ],
    icon: Guitar
  },
  {
    id: "brief-crime-drama",
    title: "Funk Songs ala James Brown — Crime Drama Series",
    client: "Crime Drama Series (Undisclosed Network)",
    type: "Scripted Television",
    deadline: "2026-05-06",
    payout: "$15,000",
    payoutRaw: 15000,
    status: "ACTIVE",
    priority: "HIGH",
    description: "A music supervisor is seeking funk and soul tracks with a 70s feel for a crime drama series. Looking for songs with a boss energy, similar in vibe to James Brown or The J.B.'s. The songs should be commanding with strong groove and attitude. Lyrics can touch on themes like being the boss, survival, or crime, but should feel natural and not overly on-the-nose.",
    genres: ["Funk", "Soul", "Afro-Funk", "Jazz Funk"],
    vocal: "Male",
    use: "TV Broadcast, Internet, Paid Media",
    length: "Full (flexible length)",
    versions: "Cutdowns, multiple edits, versions, lifts",
    paidMedia: "Yes",
    term: "Worldwide, All Media, In Perpetuity",
    references: [
      "James Brown & The J.B.'s — The Boss",
      "Isaac Hayes — Theme from Shaft",
      "Bobby Womack — Across 110th Street"
    ],
    icon: Film
  },
  {
    id: "brief-feature-film-club",
    title: "Russian Pop Songs — Feature Film Club Scene",
    client: "Feature Film (Undisclosed Studio)",
    type: "Feature Film",
    deadline: "2026-05-03",
    payout: "$15,000",
    payoutRaw: 15000,
    status: "ACTIVE",
    priority: "HIGH",
    description: "A music supervisor is seeking a Russian pop song for a club night opener in a feature film. The song should be in Russian language. Because it's for an opener, the song should start off energetic, with a strong groove that sets the tone for a nightlife scene. Male and female vocals are both welcome.",
    genres: ["Russian Pop", "Russian Dance"],
    vocal: "Male or Female",
    use: "Film, Internet",
    length: "Full (flexible length)",
    versions: "Cutdowns, multiple edits, versions, lifts",
    paidMedia: "",
    term: "Worldwide, All Media, In Perpetuity",
    references: [
      "SABI, MIA BOYKA — Bazovyy minimum",
      "Dzhigan, Artik & Asti, NILETTO — Khudi"
    ],
    icon: Globe
  },
  {
    id: "brief-pet-food-commercial",
    title: "Energetic Rock Instrumental — Brand Commercial: Pet Food",
    client: "Pet Food Brand (Undisclosed)",
    type: "Brand Commercial",
    deadline: "2026-05-03",
    payout: "$30,000",
    payoutRaw: 30000,
    status: "ACTIVE",
    priority: "HIGH",
    description: "A creative director is seeking an energetic rock instrumental for a Pet Food commercial. The music should feel amped up, bold, and high-impact, with strong guitars and driving rhythm. The ask here is for something heavy and exciting that brings energy and helps elevate the intensity of the spot. Track should not be dark. Instrumental only.",
    genres: ["Rock", "Classic Rock", "Hard Rock"],
    vocal: "Instrumental Only",
    use: "TV Broadcast, Internet, Paid Media",
    length: "Full (flexible length)",
    versions: "Cutdowns, multiple edits, versions, lifts",
    paidMedia: "Yes",
    term: "Worldwide, All Media, In Perpetuity",
    references: [
      "AC/DC — Back In Black (instrumental)",
      "Motley Crue — Kickstart My Heart (instrumental)"
    ],
    icon: Tv
  }
];

const totalPipeline = activeBriefs.reduce((sum, b) => sum + b.payoutRaw, 0);

const getPriorityColor = (priority) => {
  switch (priority) {
    case "CRITICAL": return "text-red-400 bg-red-400/10 border-red-400/30";
    case "HIGH": return "text-orange-400 bg-orange-400/10 border-orange-400/30";
    case "MEDIUM": return "text-yellow-400 bg-yellow-400/10 border-yellow-400/30";
    default: return "text-[rgb(161,161,170)] bg-[rgba(161,161,170,0.1)] border-[rgba(161,161,170,0.3)]";
  }
};

const getDaysUntilDeadline = (deadline) => {
  const today = new Date();
  const deadlineDate = new Date(deadline + "T23:59:00");
  const diffDays = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
  return diffDays;
};

const BriefsPage = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const metaRobots = document.createElement("meta");
    metaRobots.name = "robots";
    metaRobots.content = "noindex, nofollow";
    document.head.appendChild(metaRobots);
    document.title = "Brief Tracker | ItzGreen Music Admin";
    return () => { document.head.removeChild(metaRobots); };
  }, []);

  useEffect(() => {
    trackingService.initialize();
    trackingService.track("briefs_page_access", { timestamp: new Date().toISOString() });
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (password === vaultConfig.password) {
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <main className="min-h-screen bg-[rgb(10,10,12)] text-white font-mono selection:bg-[rgb(218,255,1)] selection:text-black">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        <AnimatePresence mode="wait">
          {!unlocked ? (
            <motion.div key="locked" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.05, filter: "blur(10px)" }} className="min-h-[80vh] flex flex-col items-center justify-center max-w-md mx-auto">
              <div className="w-16 h-16 bg-[rgba(218,255,1,0.1)] border border-[rgb(218,255,1)] rounded-full flex items-center justify-center mb-8 animate-pulse">
                <Crosshair size={32} className="text-[rgb(218,255,1)]" />
              </div>
              <h1 className="text-2xl font-bold tracking-[0.2em] mb-2 text-[rgb(218,255,1)]">BRIEF TRACKER</h1>
              <p className="text-[rgb(100,100,110)] text-xs tracking-widest mb-10">RESTRICTED — EXECUTIVE ACCESS ONLY</p>
              <form onSubmit={handleUnlock} className="w-full space-y-4">
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="ENTER ACCESS KEY" className={`bg-[rgb(20,20,25)] border-2 text-center text-lg tracking-[0.3em] h-14 rounded-none transition-all focus:ring-0 ${error ? "border-red-500 text-red-500" : "border-[rgb(63,63,63)] focus:border-[rgb(218,255,1)] text-[rgb(218,255,1)] placeholder:text-[rgb(60,60,70)]"}`} style={{ fontFamily: "'JetBrains Mono', monospace" }} autoFocus data-testid="briefs-password-input" />
                <Button type="submit" className="w-full bg-[rgb(218,255,1)] text-black font-bold tracking-[0.2em] h-12 rounded-none hover:bg-white transition-colors" data-testid="briefs-authenticate-btn">
                  <Unlock size={16} className="mr-3" />AUTHENTICATE
                </Button>
              </form>
              {error && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 flex items-center gap-2 text-red-500 text-xs tracking-widest">
                  <AlertTriangle size={14} /><span>ACCESS DENIED</span>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              {/* Header */}
              <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-[rgba(255,255,255,0.1)] pb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[rgb(218,255,1)] animate-pulse" />
                    <span className="text-[rgb(218,255,1)] text-xs tracking-[0.2em] font-bold">ACTIVE PIPELINE</span>
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight text-white">PLACEMENT BRIEF TRACKER</h1>
                </div>
                <div className="flex items-center gap-4 text-xs text-[rgb(100,100,110)]">
                  <Link to="/admin/analytics" data-testid="nav-analytics-from-briefs">
                    <div className="flex items-center gap-2 px-4 py-2 bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.1)] rounded-md cursor-pointer hover:border-[rgba(218,255,1,0.3)] transition-colors">
                      <Activity size={14} /><span>ANALYTICS</span>
                    </div>
                  </Link>
                  <Link to="/admin/link-generator" data-testid="nav-linkgen-from-briefs">
                    <div className="flex items-center gap-2 px-4 py-2 bg-[rgba(218,255,1,0.06)] border border-[rgba(218,255,1,0.3)] rounded-md cursor-pointer hover:bg-[rgba(218,255,1,0.12)] transition-colors">
                      <Target size={14} className="text-[rgb(218,255,1)]" /><span className="text-[rgb(218,255,1)]">LINK GENERATOR</span>
                    </div>
                  </Link>
                </div>
              </header>

              {/* Pipeline KPI */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[rgb(20,20,25)] border border-[rgba(218,255,1,0.2)] p-6 rounded-lg relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[rgb(218,255,1)] to-transparent opacity-50" />
                  <span className="text-[rgb(100,100,110)] text-[10px] tracking-[0.2em] font-bold block mb-4">TOTAL PIPELINE VALUE</span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-[rgb(218,255,1)]">${(totalPipeline / 1000).toFixed(0)}K</span>
                    <span className="text-xs text-[rgb(74,222,128)] bg-[rgba(74,222,128,0.1)] px-2 py-0.5 rounded">ACTIVE</span>
                  </div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.08)] p-6 rounded-lg relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[rgb(218,255,1)] to-transparent opacity-50" />
                  <span className="text-[rgb(100,100,110)] text-[10px] tracking-[0.2em] font-bold block mb-4">ACTIVE BRIEFS</span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white">{activeBriefs.length}</span>
                    <span className="text-xs text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.1)] px-2 py-0.5 rounded">TRACKING</span>
                  </div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.08)] p-6 rounded-lg relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-red-500 to-transparent opacity-50" />
                  <span className="text-red-400 text-[10px] tracking-[0.2em] font-bold block mb-4">NEAREST DEADLINE</span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white">{Math.max(0, Math.min(...activeBriefs.map(b => getDaysUntilDeadline(b.deadline))))}</span>
                    <span className="text-xs text-red-400 bg-[rgba(239,68,68,0.1)] px-2 py-0.5 rounded">DAYS</span>
                  </div>
                </motion.div>
              </div>

              {/* Brief Cards */}
              <div className="space-y-6">
                {activeBriefs.map((brief, index) => {
                  const daysLeft = getDaysUntilDeadline(brief.deadline);
                  const isUrgent = daysLeft <= 3;
                  const IconComp = brief.icon;
                  return (
                    <motion.div key={brief.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }} className={`bg-[rgb(20,20,25)] border rounded-lg overflow-hidden ${isUrgent ? "border-red-500/30" : "border-[rgba(255,255,255,0.08)]"}`} data-testid={`brief-card-${brief.id}`}>
                      {/* Brief Header Bar */}
                      <div className={`px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 ${isUrgent ? "bg-[rgba(239,68,68,0.05)]" : "bg-[rgba(255,255,255,0.02)]"} border-b border-[rgba(255,255,255,0.05)]`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isUrgent ? "bg-red-500/10" : "bg-[rgba(218,255,1,0.1)]"}`}>
                            <IconComp size={20} className={isUrgent ? "text-red-400" : "text-[rgb(218,255,1)]"} />
                          </div>
                          <div>
                            <h2 className="text-white text-base font-bold">{brief.title}</h2>
                            <span className="text-[rgb(100,100,110)] text-xs">{brief.client} | {brief.type}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`${getPriorityColor(brief.priority)} text-[10px] font-bold px-3 py-1 rounded`}>{brief.priority}</Badge>
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-bold ${isUrgent ? "bg-red-500/10 text-red-400 border border-red-500/30" : "bg-[rgba(255,255,255,0.05)] text-[rgb(161,161,170)]"}`}>
                            <Clock size={12} />
                            {daysLeft > 0 ? `${daysLeft}d remaining` : "EXPIRED"}
                          </div>
                          <div className="bg-[rgba(218,255,1,0.1)] border border-[rgba(218,255,1,0.3)] text-[rgb(218,255,1)] px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2">
                            <DollarSign size={12} />{brief.payout}
                          </div>
                        </div>
                      </div>

                      {/* Brief Body */}
                      <div className="p-6">
                        <p className="text-[rgb(180,180,190)] text-sm leading-relaxed mb-5">{brief.description}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                          <div>
                            <span className="text-[rgb(80,80,90)] text-[9px] tracking-[0.2em] font-bold block mb-1">GENRE</span>
                            <div className="flex flex-wrap gap-1">
                              {brief.genres.map((g, i) => (
                                <span key={i} className="text-[10px] bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.08)] text-[rgb(161,161,170)] px-2 py-0.5 rounded">{g}</span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <span className="text-[rgb(80,80,90)] text-[9px] tracking-[0.2em] font-bold block mb-1">VOCAL</span>
                            <span className="text-[rgb(218,255,1)] text-xs font-medium flex items-center gap-1"><Mic size={10} />{brief.vocal}</span>
                          </div>
                          <div>
                            <span className="text-[rgb(80,80,90)] text-[9px] tracking-[0.2em] font-bold block mb-1">USE</span>
                            <span className="text-[rgb(161,161,170)] text-[10px]">{brief.use}</span>
                          </div>
                          <div>
                            <span className="text-[rgb(80,80,90)] text-[9px] tracking-[0.2em] font-bold block mb-1">TERM</span>
                            <span className="text-[rgb(161,161,170)] text-[10px]">{brief.term}</span>
                          </div>
                        </div>

                        {/* Reference Tracks */}
                        <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)] rounded-lg p-4">
                          <span className="text-[rgb(80,80,90)] text-[9px] tracking-[0.2em] font-bold block mb-2">REFERENCE TRACKS</span>
                          <div className="flex flex-wrap gap-2">
                            {brief.references.map((ref, i) => (
                              <span key={i} className="text-xs text-[rgb(180,160,220)] bg-[rgba(180,160,220,0.06)] border border-[rgba(180,160,220,0.15)] px-3 py-1 rounded italic">{ref}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="mt-12 pt-8 border-t border-[rgba(255,255,255,0.06)] text-center">
                <p className="text-[rgb(80,80,90)] text-[10px] tracking-[0.2em]">
                  CONFIDENTIAL EXECUTIVE INTELLIGENCE | ITZGREEN MUSIC LLC | DO NOT DISTRIBUTE
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
};

export default BriefsPage;
